import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ResponsiveDialog
 *
 * Layout strategy:
 *  - Mobile (<sm): full-screen sheet (inset-0, h-screen).
 *  - Desktop (>=sm): centered modal, max-w-4xl, capped at 92vh.
 *
 *  Internally uses a fixed flex-column shell:
 *      [Header  — flex-none, border-b]
 *      [Body    — flex-1, min-h-0, overflow-y-auto]   ← only the body scrolls
 *      [Footer  — flex-none, border-t]
 *
 *  No sticky positioning, no `display: contents` magic ⇒ no z-index/overlap
 *  problems on either platform.
 *
 *  Usage:
 *    <ResponsiveDialog open={...} onOpenChange={...}>
 *      <ResponsiveDialogHeader title="…" description="…" />
 *      <ResponsiveDialogBody>{form fields}</ResponsiveDialogBody>
 *      <ResponsiveDialogFooter>
 *        <Button variant="ghost" onClick={…}>Batal</Button>
 *        <Button type="submit">Simpan</Button>
 *      </ResponsiveDialogFooter>
 *    </ResponsiveDialog>
 */

export const ResponsiveDialog = ({ open, onOpenChange, children, testid, className, size = 'lg' }) => (
  <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogPrimitive.Content
        data-testid={testid}
        className={cn(
          // === Mobile (default): full-screen sheet ===
          'fixed inset-0 z-50 flex flex-col bg-card text-foreground outline-none',
          // === Desktop (sm+): centered modal ===
          'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:w-[calc(100vw-2rem)] sm:max-h-[92vh]',
          size === 'xl' ? 'sm:max-w-6xl' : size === '2xl' ? 'sm:max-w-7xl' : 'sm:max-w-4xl',
          'sm:rounded-xl sm:border sm:border-border sm:shadow-2xl',
          // Animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          className
        )}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);

export const ResponsiveDialogHeader = ({ title, description, className }) => (
  <div
    className={cn(
      'flex-none flex items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6 sm:py-4 sm:rounded-t-xl',
      className
    )}
  >
    <div className="min-w-0">
      <DialogPrimitive.Title className="text-base sm:text-lg font-semibold leading-tight font-heading truncate">
        {title}
      </DialogPrimitive.Title>
      {description ? (
        <DialogPrimitive.Description className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-1">
          {description}
        </DialogPrimitive.Description>
      ) : null}
    </div>
    <DialogPrimitive.Close
      className="h-9 w-9 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shrink-0"
      aria-label="Tutup"
    >
      <X className="h-5 w-5" />
    </DialogPrimitive.Close>
  </div>
);

export const ResponsiveDialogBody = ({ children, className }) => (
  <div
    className={cn(
      // The ONLY scroll container in the dialog
      'flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5 space-y-4',
      className
    )}
  >
    {children}
  </div>
);

export const ResponsiveDialogFooter = ({ children, className }) => (
  <div
    className={cn(
      'flex-none border-t border-border bg-card px-4 py-3 sm:px-6 sm:py-4 sm:rounded-b-xl',
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2',
      // iOS home-indicator safe area
      'pb-[calc(env(safe-area-inset-bottom,0)+12px)] sm:pb-4',
      className
    )}
  >
    {children}
  </div>
);
